'use client';

import { SvgAdd } from '@common/icon';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { CustomLink } from '@/app/_components/CustomLink';
import { ServiceSwitchDropdown } from '@/app/_components/ServiceSwitchDropdown';
import { DONATION_BNB_TOAST_ITEMS } from '@/app/donation/constants';
import { DONATION_ROUTES, ROUTES, SUB_DOMAIN } from '@/constants';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { gtmDatasets } from '@/utils/analytics/gtm';

const ANIMATION_DURATION = 300;

interface DonationBnbProps {
  className?: string;
  isMounted?: boolean;
}

export const DonationBnb = ({
  className,
  isMounted = true,
}: DonationBnbProps) => {
  const pathname = usePathname();
  const [menuClickAnimated, setMenuClickAnimated] = useState<{
    [key: number]: boolean;
  }>({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobile, isToss] = useDeviceInfoStore(
    useShallow(
      (state) =>
        [
          ['android', 'ios'].includes(state.osType || 'unknown'),
          state.subDomain === SUB_DOMAIN.TOSS,
        ] as const,
    ),
  );

  const handleMenuClick = (index: number) => {
    setMenuClickAnimated((prev) => ({
      ...prev,
      [index]: true,
    }));

    setTimeout(() => {
      setMenuClickAnimated((prev) => ({
        ...prev,
        [index]: false,
      }));
    }, ANIMATION_DURATION);
  };

  return (
    <nav
      aria-label="기부 내비게이션"
      className={cn(
        'relative mx-20 flex items-center rounded-pill bg-Gray-white p-8 shadow-[0px_0px_30px_0px_rgba(0,0,0,0.05),0px_16px_50px_0px_rgba(0,0,0,0.05)]',
        className,
      )}
    >
      {!isToss ? (
        <div className="relative z-50">
          <button
            type="button"
            className="relative z-50 size-40 rounded-pill bg-Gray-200 duration-150 flex-center hover:scale-110"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            aria-label="다른 서비스로 이동"
            aria-expanded={isDropdownOpen}
          >
            <SvgAdd
              className={cn(
                'size-20 text-Gray-600 transition-transform duration-200',
                {
                  'rotate-45': isDropdownOpen,
                },
              )}
            />
          </button>
          <ServiceSwitchDropdown
            isOpen={isDropdownOpen}
            onClose={() => setIsDropdownOpen(false)}
            currentService="donation"
          />
        </div>
      ) : null}
      <ul
        className="flex flex-1 items-center"
        style={{
          justifyContent: isMobile ? 'space-between' : 'space-around',
          padding: isMobile ? '' : '0 30px',
        }}
      >
        {DONATION_BNB_TOAST_ITEMS.map(({ label, route, icon: Icon }, index) => {
          const isActive = (() => {
            if (!pathname) return false;

            if (route.pathname === DONATION_ROUTES.HOME.pathname) {
              return pathname === route.pathname;
            }

            if (route.pathname === DONATION_ROUTES.MY.pathname) {
              return (
                pathname === ROUTES.MY.pathname ||
                pathname.includes(route.pathname)
              );
            }

            return pathname.includes(route.pathname);
          })();

          const clickFnbDataset = gtmDatasets.clickFnb({
            menu_name: label,
          });

          return (
            <li key={label} className="w-53">
              <CustomLink
                href={route.pathname}
                aria-label={`${label} 페이지로 이동`}
                prefetch
                className={cn(
                  'flex w-full flex-col items-center duration-150 hover:scale-110',
                  menuClickAnimated[index] && 'animate-jump',
                  isMounted ? 'scale-100' : 'scale-0',
                )}
                style={{
                  animationDuration: `${ANIMATION_DURATION}ms`,
                  transitionDelay: `${(index + 1) * 60}ms`,
                  transitionProperty: 'transform',
                  transitionDuration: '300ms',
                }}
                onClick={() => handleMenuClick(index)}
                {...clickFnbDataset}
              >
                <Icon
                  className={cn('size-28 text-Gray-400', {
                    'text-Gray-black': isActive,
                  })}
                />
                <p
                  className={cn(
                    'whitespace-nowrap text-center font-500 text-Gray-550 typo-caption2',
                    {
                      'font-600 text-Gray-900': isActive,
                    },
                  )}
                >
                  {label}
                </p>
              </CustomLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
